import { useCallback } from 'react'
import { useLanguage } from './LanguageProvider'
import { DE } from './translations'

// Remember which missing keys were already warned about, so the dev console
// isn't spammed on every re-render.
const warned = new Set<string>()

// DE is a Record<string, string>, which would tell TypeScript that every lookup
// hits. Reading it through this view keeps the missing-key branch honest.
const dictionary: Record<string, string | undefined> = DE

/**
 * English-as-key translation hook.
 *
 * `t('Dashboard')` returns the English source string in English mode (identity —
 * the English UI stays byte-identical) and the German translation in German
 * mode. If a translation is missing it falls back to the English string and (in
 * dev) logs a one-time warning so the gap can be closed.
 *
 * IMPORTANT: only wrap STATIC UI labels. Never wrap backend- or model-generated
 * text (extraction warnings, API error messages, Flixpector rationales …) or
 * master data (supplier names, legal entities, PO descriptions).
 */
export function useT() {
  const { lang } = useLanguage()

  // `t` is stable per language (memoised on `lang`), so it can safely be listed
  // in useMemo/useCallback dependency arrays: dependent values then recompute
  // exactly on a language switch, not on every render.
  const t = useCallback(
    (en: string): string => {
      if (lang === 'en') return en
      const de = dictionary[en]
      if (de === undefined) {
        if (import.meta.env.DEV && !warned.has(en)) {
          warned.add(en)
          console.warn('[i18n] Missing DE translation for:', JSON.stringify(en))
        }
        return en
      }
      return de
    },
    [lang],
  )

  return { t, lang }
}
